import React, { useEffect, useState } from "react";
import { Table, Button, message, Space, Input, Row, Col, Modal } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined, SearchOutlined, EditTwoTone, CloseOutlined, UserOutlined, UserDeleteOutlined } from "@ant-design/icons";
import UserForm from "./UserForm";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";

export default function User() {
  const [users, setUsers] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isPermissionModalVisible, setIsPermissionModalVisible] = useState(false);
  const [userToTogglePermission, setUserToTogglePermission] = useState(null);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [userToToggleStatus, setUserToToggleStatus] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    getUsers();
  }, [paginationInfo.current, paginationInfo.pageSize, searchQuery]);

  useEffect(() => {
    const userData = localStorage.getItem("userData");
    const data = JSON.parse(userData);
    setUserRole(data);
  }, []);

  const getUsers = async () => {
    try {
      setLoading(true);
      const { current, pageSize } = paginationInfo;
      const response = await API.get("/users/paginate", { params: { page: current, limit: pageSize, search: searchQuery } });
      const { users: getUsers, pagination } = response.data;
      setUsers(getUsers);
      setPaginationInfo((prev) => ({
        ...prev,
        total: pagination.total,
      }));
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermission = async (userId, currentPermission) => {
    try {
      await API.put("/users/editPermission", {
        userIds: [userId],
        editPermission: !currentPermission,
      });

      // Update the state to reflect the change
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === userId ? { ...user, editPermission: !currentPermission } : user
        )
      );
      message.success("Edit permission updated successfully.");
    } catch (error) {
      console.error("Error updating permission:", error);
      message.error("Failed to update permission.");
    }
  };

  const confirmEditPermission = (user) => {
    setUserToTogglePermission(user);
    setIsPermissionModalVisible(true);
  };

  const handleConfirmEditPermission = async () => {
    if (userToTogglePermission) {
      await handleEditPermission(userToTogglePermission._id, userToTogglePermission.editPermission);
      setIsPermissionModalVisible(false);
      setUserToTogglePermission(null);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await API.put(`/users/toggle-status/${userId}`, {
        isDeleted: !currentStatus,
      });

      // Update the state to reflect the change
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === userId ? { ...user, isDeleted: !currentStatus } : user
        )
      );
      message.success(`User ${!currentStatus ? 'deactivated' : 'activated'} successfully.`);
    } catch (error) {
      console.error("Error toggling user status:", error);
      message.error("Failed to update user status.");
    }
  };

  const confirmToggleStatus = (user) => {
    setUserToToggleStatus(user);
    setIsStatusModalVisible(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (userToToggleStatus) {
      await handleToggleStatus(userToToggleStatus._id, userToToggleStatus.isDeleted);
      setIsStatusModalVisible(false);
      setUserToToggleStatus(null);
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) => {
        const current = paginationInfo.current;
        const pageSize = paginationInfo.pageSize;
        return (current - 1) * pageSize + index + 1;
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: ["role", "name"],
      key: "role",
    },
    {
      title: "Store",
      dataIndex: ["storeId", "name"],
      key: "store",
      render: (storeName) => storeName || "N/A",
    },
    {
      title: "Edit Permission",
      dataIndex: "editPermission",
      key: "editPermission",
      align: "center",
      render: (editPermission) => (
        <span style={{ 
          color: editPermission ? "#52c41a" : "#ff4d4f",
          fontWeight: "bold"
        }}>
          {editPermission ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => date ? new Date(date).toLocaleDateString() : "N/A",
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => date ? new Date(date).toLocaleDateString() : "N/A",
    },
    {
      title: "Status",
      dataIndex: "isDeleted",
      key: "status",
      align: "center",
      render: (isDeleted) => (
        <span style={{ 
          color: isDeleted ? "#ff4d4f" : "#52c41a",
          fontWeight: "bold",
          padding: "4px 8px",
          borderRadius: "4px",
          backgroundColor: isDeleted ? "#fff2f0" : "#f6ffed",
          border: `1px solid ${isDeleted ? "#ffccc7" : "#b7eb8f"}`
        }}>
          {isDeleted ? "Inactive" : "Active"}
        </span>
      ),
    },
    userRole.editPermission == true &&{
      title: "Toggle Permission",
      key: "permission",
      align: "center",
      render: (record) => (
        <Button
          color={record.editPermission ? "danger" : "default"}
          variant="outlined"
          onClick={() => confirmEditPermission(record)}
          icon={record.editPermission ? <CloseOutlined  style={{ color: "red" }}/> :   <EditTwoTone />}         
        />
      ),
    },
    userRole.editPermission == true &&{
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  setIsEdit(true);
                  setSelectedUser(record);
                  setOpenDrawer(true);
                }}
                title="Edit User"
              />
              <Button
                type={record.isDeleted ? "primary" : "default"}
                danger={!record.isDeleted}
                icon={record.isDeleted ? <UserOutlined /> : <UserDeleteOutlined />}
                onClick={() => confirmToggleStatus(record)}
                title={record.isDeleted ? "Activate User" : "Deactivate User"}
              />
              {/* <Button
                danger
                icon={<DeleteOutlined />}
                style={{ marginLeft: 8 }}
                onClick={() => {
                  setUserToDelete(record);
                  setIsDeleteModalVisible(true);
                }}
              /> */}
            </>
          
        </div>
      ),
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      {userRole.role === "admin" && (
        <Row gutter={16} style={{ marginBottom: 16, justifyContent: "flex-end", marginRight: 10 }}>
          <Col xs={24} sm={16} md={18} lg={20}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedUser(null);
                  setIsEdit(false);
                  setOpenDrawer(true);
                }}
              >
                Add New User
              </Button>

              <Input
                placeholder="Search users"
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // Reset pagination to page 1 when searching
                  setPaginationInfo(prev => ({ ...prev, current: 1 }));
                }}
                style={{ width: "100%" }}
              />
            </Space>
          </Col>
        </Row>
      )}

      {openDrawer && (
        <UserForm
          open={openDrawer}
          setOpen={setOpenDrawer}
          selectedUser={selectedUser}
          onEdit={isEdit ? getUsers : getUsers}
        />
      )}

      <CustomTable
        columns={columns}
        dataSource={users}
        onPaginationChange={(pagination) => {
          setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
          });
        }}
        loading={loading}
        style={{ padding: "0 20px" }}
        total={paginationInfo.total}
      />

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={() => {
          API.delete(`/users/${userToDelete._id}`)
            .then(() => {
              setUsers((prev) => prev.filter((user) => user._id !== userToDelete._id));
              message.success("User deleted successfully.");
            })
            .catch((err) => {
              console.error("Error deleting user:", err);
              message.error("Failed to delete user.");
            })
            .finally(() => setIsDeleteModalVisible(false));
        }}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this user? This action cannot be undone."
      />

      {/* Edit Permission Confirmation Modal */}
      <Modal
        title="Confirm Edit Permission Change"
        open={isPermissionModalVisible}
        onOk={handleConfirmEditPermission}
        onCancel={() => {
          setIsPermissionModalVisible(false);
          setUserToTogglePermission(null);
        }}
        okText="Confirm"
        cancelText="Cancel"
        okButtonProps={{
          danger: userToTogglePermission?.editPermission === true,
          type: userToTogglePermission?.editPermission === true ? "primary" : "default"
        }}
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ marginBottom: "16px", fontSize: "16px" }}>
            <strong>⚠️ Warning:</strong> You are about to change edit permissions for:
          </p>
          <div style={{ 
            backgroundColor: "#f5f5f5", 
            padding: "12px", 
            borderRadius: "6px", 
            marginBottom: "16px" 
          }}>
            <p><strong>User:</strong> {userToTogglePermission?.name}</p>
            <p><strong>Email:</strong> {userToTogglePermission?.email}</p>
            <p><strong>Current Permission:</strong> 
              <span style={{ 
                color: userToTogglePermission?.editPermission ? "#52c41a" : "#ff4d4f",
                fontWeight: "bold",
                marginLeft: "8px"
              }}>
                {userToTogglePermission?.editPermission ? "Has Edit Permission" : "No Edit Permission"}
              </span>
            </p>
          </div>
          <p style={{ 
            color: userToTogglePermission?.editPermission ? "#ff4d4f" : "#1890ff",
            fontWeight: "bold",
            fontSize: "14px"
          }}>
            {userToTogglePermission?.editPermission 
              ? "❌ This will REMOVE edit permissions from the user. They will no longer be able to modify data."
              : "✅ This will GRANT edit permissions to the user. They will be able to modify data."
            }
          </p>
          <p style={{ marginTop: "12px", color: "#666", fontSize: "13px" }}>
            Are you sure you want to proceed?
          </p>
        </div>
      </Modal>

      {/* User Status Toggle Confirmation Modal */}
      <Modal
        title="Confirm User Status Change"
        open={isStatusModalVisible}
        onOk={handleConfirmToggleStatus}
        onCancel={() => {
          setIsStatusModalVisible(false);
          setUserToToggleStatus(null);
        }}
        okText="Confirm"
        cancelText="Cancel"
        okButtonProps={{
          danger: userToToggleStatus?.isDeleted === false,
          type: userToToggleStatus?.isDeleted === false ? "primary" : "default"
        }}
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ marginBottom: "16px", fontSize: "16px" }}>
            <strong>⚠️ Warning:</strong> You are about to change the status for:
          </p>
          <div style={{ 
            backgroundColor: "#f5f5f5", 
            padding: "12px", 
            borderRadius: "6px", 
            marginBottom: "16px" 
          }}>
            <p><strong>User:</strong> {userToToggleStatus?.name}</p>
            <p><strong>Email:</strong> {userToToggleStatus?.email}</p>
            <p><strong>Role:</strong> {userToToggleStatus?.role?.name}</p>
            <p><strong>Current Status:</strong> 
              <span style={{ 
                color: userToToggleStatus?.isDeleted ? "#ff4d4f" : "#52c41a",
                fontWeight: "bold",
                marginLeft: "8px"
              }}>
                {userToToggleStatus?.isDeleted ? "Inactive" : "Active"}
              </span>
            </p>
          </div>
          <p style={{ 
            color: userToToggleStatus?.isDeleted ? "#1890ff" : "#ff4d4f",
            fontWeight: "bold",
            fontSize: "14px"
          }}>
            {userToToggleStatus?.isDeleted 
              ? "✅ This will ACTIVATE the user. They will be able to log in and access the system."
              : "❌ This will DEACTIVATE the user. They will NOT be able to log in or access the system."
            }
          </p>
          <p style={{ marginTop: "12px", color: "#666", fontSize: "13px" }}>
            Are you sure you want to proceed?
          </p>
        </div>
      </Modal>
    </div>
  );
}
